import { router } from '@inertiajs/react'
import { Check } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/Select'
import { useRouteParams } from '@/hooks/useRoutes'
import routes from '@/routeHelpers'
import { activeBrandParam } from '@/lib/activeBrand'
import type { HelpCenterSettings, HelpCenterChat } from '@/pages/HelpCenter/types'

interface WebWidget {
  id: string
  name: string
}

interface HelpCenterIntegrationsCardProps {
  settings: HelpCenterSettings
  webWidgets: WebWidget[]
}

const NO_WIDGET = 'none'

export function HelpCenterIntegrationsCard({ settings, webWidgets }: HelpCenterIntegrationsCardProps) {
  const { withAccount } = useRouteParams()

  const [ga4, setGa4] = useState(settings.ga4_measurement_id ?? '')
  const [chat, setChat] = useState<HelpCenterChat>(settings.chat ?? {})

  const save = () => {
    router.patch(
      routes.helpCenterSettings.update.path(withAccount()),
      {
        help_center_setting: {
          ga4_measurement_id: ga4.trim(),
          chat: chat as unknown as Record<string, string>,
        },
        ...activeBrandParam(),
      },
      { preserveScroll: true },
    )
  }

  return (
    <div className="max-w-3xl space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Analytics</CardTitle>
          <CardDescription>Track visits to your public help center with Google Analytics 4.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <Label>GA4 Measurement ID</Label>
          <Input
            value={ga4}
            onChange={e => setGa4(e.target.value)}
            placeholder="G-XXXXXXXXXX"
            className="max-w-[260px] font-mono"
          />
          <p className="text-xs text-muted-foreground">
            Found in Google Analytics under Admin → Data Streams. Loads gtag.js on the public help center only. Leave blank to disable.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Chat widget</CardTitle>
          <CardDescription>Show your live chat widget on the help center so visitors can reach you.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Widget</Label>
            <Select
              value={chat.channel_id ?? NO_WIDGET}
              onValueChange={v => setChat({ ...chat, channel_id: v === NO_WIDGET ? null : v })}
            >
              <SelectTrigger><SelectValue placeholder="No chat widget" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_WIDGET}>No chat widget</SelectItem>
                {webWidgets.map(w => (
                  <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {webWidgets.length === 0 && (
              <p className="text-xs text-muted-foreground">No web widgets yet — create one under Settings → Channels.</p>
            )}
          </div>

          {chat.channel_id && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Placement</Label>
                <Select value={chat.position ?? 'bottom-right'} onValueChange={v => setChat({ ...chat, position: v as HelpCenterChat['position'] })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bottom-right">Bottom right</SelectItem>
                    <SelectItem value="bottom-left">Bottom left</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Launcher</Label>
                <Select value={chat.launcher ?? 'always'} onValueChange={v => setChat({ ...chat, launcher: v as HelpCenterChat['launcher'] })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="always">Always show bubble</SelectItem>
                    <SelectItem value="button">Only open from “Contact us”</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-end border-t border-border pt-4">
        <Button onClick={save}><Check className="size-4" /> Save changes</Button>
      </div>
    </div>
  )
}
